import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Clapperboard } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const BASE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/";

const SLIDES = [
  { src: `${BASE}0491db9be98313a7_WhatsApp%20Image%202026-06-14%20at%2019.53.12%20%281%29.jpeg`, title: "The Final Lap begins", caption: "Arriving at the 2026 Grade 12 English Camp with energy to spare." },
  { src: `${BASE}8145566fd632e9cd_WhatsApp%20Image%202026-06-14%20at%2019.52.03%20%281%29.jpeg`, title: "Ready to learn", caption: "Snack packs in hand, learners gather at the camp gates." },
  { src: `${BASE}a6e809aa8ec0c2d5_WhatsApp%20Image%202026-06-14%20at%2019.53.08.jpeg`, title: "Camp masterclass", caption: "An educator leads an English session at camp." },
  { src: `${BASE}f772a1e85e4c0257_WhatsApp%20Image%202026-06-14%20at%2019.53.09%20%281%29.jpeg`, title: "Final Lap focus", caption: "Grade 12 learners locked in during a camp masterclass." },
  { src: `${BASE}98c391e718ec2711_WhatsApp%20Image%202026-06-14%20at%2019.52.06.jpeg`, title: "Full classroom, full commitment", caption: "The 2026 camp packs the room." },
  { src: `${BASE}926a3dfe6347ca8e_WhatsApp%20Image%202026-06-14%20at%2019.52.07%20%281%29.jpeg`, title: "Every seat taken", caption: "A full day of study, one page at a time." },
  { src: `${BASE}70c6181f57113746_WhatsApp%20Image%202026-06-14%20at%2019.52.04%20%281%29.jpeg`, title: "Camp crew", caption: "Celebrating together between sessions." },
  { src: `${BASE}ed3078d6a4b9d6f5_WhatsApp%20Image%202026-06-14%20at%2019.52.03%20%282%29.jpeg`, title: "Mountain air, clear minds", caption: "The Final Lap crew takes a break, mist and all." },
  { src: `${BASE}edfcc72e41f08497_WhatsApp%20Image%202026-06-14%20at%2019.53.10%20%282%29.jpeg`, title: "New friends on the trail", caption: "A gentle meet and greet during the outdoor programme." },
  { src: `${BASE}8f79687c6cd2a3f0_WhatsApp%20Image%202026-06-14%20at%2019.52.03.jpeg`, title: "Golden hour", caption: "The Final Lap class of 2026, one sunset closer to their goals." },
];

const DURATION = 5000;

export const CampReel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), DURATION);
    return () => clearInterval(t);
  }, [paused]);

  const slide = SLIDES[index];
  const drift = index % 2 === 0 ? { x: [0, -18], y: [0, -12] } : { x: [0, 18], y: [0, 10] };

  return (
    <section className="bg-coal-dark grain py-16" data-testid="camp-reel-section">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow light>Now showing</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-sand sm:text-5xl">
              Final Lap: <span className="text-gold">the 2026 camp reel</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-sand/65">
              Buses, masterclasses and mountain air, the Grade 12 English Camp in motion.
            </p>
          </div>
          <Link
            to="/edupower13"
            data-testid="camp-reel-more-link"
            className="group inline-flex items-center gap-2 rounded-full border border-sand/30 px-5 py-2.5 font-heading text-sm font-bold text-sand transition-colors hover:bg-sand hover:text-coal-dark"
          >
            <Clapperboard size={15} />
            More from camp
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="relative mt-10 overflow-hidden rounded-[2.5rem] border border-sand/10 shadow-[0_30px_90px_-30px_rgba(0,0,0,0.8)]"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="camp-reel-frame"
          >
            <div className="relative aspect-[4/3] w-full sm:aspect-[16/8]">
              <AnimatePresence>
                <motion.img
                  key={slide.src}
                  src={slide.src}
                  alt={slide.title}
                  initial={{ opacity: 0, scale: 1.06, x: 0, y: 0 }}
                  animate={{ opacity: 1, scale: 1.2, ...drift }}
                  exit={{ opacity: 0 }}
                  transition={{
                    opacity: { duration: 0.9, ease: "easeInOut" },
                    scale: { duration: DURATION / 1000 + 1.2, ease: "linear" },
                    x: { duration: DURATION / 1000 + 1.2, ease: "linear" },
                    y: { duration: DURATION / 1000 + 1.2, ease: "linear" },
                  }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-coal-dark/85 via-transparent to-coal-dark/30" />

              <div className="absolute left-6 top-6 rounded-full bg-ember px-4 py-1.5 font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-sand">
                Final Lap 2026
              </div>

              <div className="absolute inset-x-6 bottom-6 sm:inset-x-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={slide.title}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="font-display text-2xl font-extrabold text-sand sm:text-3xl" data-testid="camp-reel-title">
                      {slide.title}
                    </h3>
                    <p className="mt-1 max-w-lg text-xs leading-relaxed text-sand/75 sm:text-sm" data-testid="camp-reel-caption">
                      {slide.caption}
                    </p>
                  </motion.div>
                </AnimatePresence>

                <div className="mt-5 flex items-center gap-2" data-testid="camp-reel-progress">
                  {SLIDES.map((s, i) => (
                    <button
                      key={s.src}
                      data-testid={`camp-reel-dot-${i}`}
                      onClick={() => setIndex(i)}
                      aria-label={`Go to slide ${i + 1}: ${s.title}`}
                      className="group relative h-1.5 flex-1 overflow-hidden rounded-full bg-sand/20"
                    >
                      <span
                        className={`absolute inset-y-0 left-0 rounded-full bg-gold transition-[width] duration-300 ${
                          i < index ? "w-full" : i === index ? "w-full" : "w-0"
                        } ${i === index ? "opacity-100" : i < index ? "opacity-60" : ""}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="absolute right-6 top-6 rounded-full border border-sand/25 bg-coal-dark/50 px-3.5 py-1.5 font-mono-brand text-[11px] font-bold tracking-widest text-sand backdrop-blur" data-testid="camp-reel-counter">
                {String(index + 1).padStart(2, "0")} / {SLIDES.length}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
